/** 闯关地图：竖向蛇形小路布局 */

export const LAYOUT = {
  TOP_PAD: 28,
  BOTTOM_PAD: 128,
  SIDE_PAD: 72,
  NODE_SIZE: 64,
  /** 同阶段节点垂直间距；给星级牌和弯曲路径留出空隙 */
  NODE_GAP_Y: 128,
  /** 阶段之间额外间距；稀疏路径优先紧凑呈现 */
  STAGE_GAP_Y: 32,
  /** Sticky 阶段横幅高度（布局占位） */
  STAGE_BANNER_H: 44,
  COLLAPSED_PEEK_GAP: 0,
  /** 蛇形左右振幅相对半宽 */
  S_AMPLITUDE: 0.34,
}

/**
 * @param {Array<{ no: number, title?: string, items: Array<{ key: string }> }>} sections
 * @param {number} width
 * @param {{ collapsedStageKeys?: Set<string> }} [options]
 */
export function computeQuestLayout(sections, width = 640, options = {}) {
  const {
    TOP_PAD,
    BOTTOM_PAD,
    SIDE_PAD,
    NODE_SIZE,
    NODE_GAP_Y,
    STAGE_GAP_Y,
    STAGE_BANNER_H,
    S_AMPLITUDE,
  } = LAYOUT

  const collapsedStageKeys = options.collapsedStageKeys || new Set()
  const safeWidth = Math.max(320, Number(width) || 640)
  const centerX = safeWidth / 2
  const amplitude = Math.max(48, (safeWidth / 2 - SIDE_PAD) * S_AMPLITUDE * 2)

  const nodes = []
  const stages = []
  const pathSegments = []

  let yCursor = TOP_PAD
  let globalIndex = 0
  const activeSections = (sections || []).filter((s) => (s.items || []).length > 0)
  let prevNode = null

  activeSections.forEach((section, stageIndex) => {
    const items = section.items || []
    const stageKey = `${section.no ?? 'x'}-${stageIndex}`
    const collapsed = collapsedStageKeys.has(stageKey) && items.length > 1
    const visibleItems = collapsed ? [items[0]] : items

    const bannerTop = yCursor
    yCursor += STAGE_BANNER_H + 16
    const firstNodeY = yCursor + NODE_SIZE / 2
    const stageNodes = []

    visibleItems.forEach((item, itemIndex) => {
      const zig = globalIndex % 2 === 0 ? -1 : 1
      const x = centerX + zig * amplitude
      if (prevNode) {
        const span = Math.abs(x - prevNode.x)
        if (span > 160) yCursor += Math.min(96, Math.round((span - 160) * 0.32))
      }
      const y = yCursor + NODE_SIZE / 2
      const labelSide = zig < 0 ? 'right' : 'left'
      const node = {
        key: item.key,
        item,
        x,
        y,
        stageIndex,
        itemIndex,
        globalIndex,
        stageKey,
        collapsedPeek: collapsed,
        hiddenCount: collapsed ? items.length - 1 : 0,
        labelSide,
      isActive: Boolean(item?.progress?.isActive),
      isMilestone: false,
    }
      nodes.push(node)
      stageNodes.push(node)

      if (prevNode) {
        pathSegments.push({
          x1: prevNode.x,
          y1: prevNode.y,
          x2: x,
          y2: y,
          kind: prevNode.stageIndex === stageIndex ? 'intra' : 'inter',
          fromStage: prevNode.stageIndex,
          toStage: stageIndex,
        })
      }
      prevNode = node
      yCursor += NODE_GAP_Y
      globalIndex += 1
    })

    const lastNode = stageNodes[stageNodes.length - 1]
    const bandBottom = (lastNode?.y || firstNodeY) + NODE_SIZE / 2 + 28

    // 阶段标签贴近本阶段的首关左侧；节点位于左侧时回退到地图左边缘。
    const labelLeft = Math.max(10, Math.min(
      safeWidth - 168,
      (stageNodes[0]?.x ?? SIDE_PAD) - NODE_SIZE / 2 - 142,
    ))

    if (stageNodes.length) stageNodes[stageNodes.length - 1].isMilestone = true

    stages.push({
      no: section.no,
      title: section.title || '',
      stageIndex,
      stageKey,
      itemCount: items.length,
      collapsed,
      bannerTop,
      bannerHeight: STAGE_BANNER_H,
      x: SIDE_PAD / 2,
      y: bannerTop,
      labelLeft,
      band: {
        top: bannerTop,
        left: 0,
        width: safeWidth,
        height: STAGE_BANNER_H,
        labelWidth: safeWidth,
      },
      firstNodeY,
      lastNodeY: lastNode?.y ?? firstNodeY,
      entry: stageNodes[0] ? { x: stageNodes[0].x, y: stageNodes[0].y } : null,
      exit: lastNode ? { x: lastNode.x, y: lastNode.y } : null,
      nodeKeys: stageNodes.map((n) => n.key),
    })

    // 单关阶段无需为内部节点预留空白，进一步压缩试用课等稀疏路径。
    const stageGap = items.length === 1 ? Math.max(20, STAGE_GAP_Y - 8) : STAGE_GAP_Y
    yCursor = bandBottom + stageGap
  })

  const height = nodes.length
    ? Math.max(...nodes.map((n) => n.y)) + NODE_SIZE / 2 + BOTTOM_PAD
    : TOP_PAD + BOTTOM_PAD

  stages.forEach((stage, index) => {
    const nextTop = stages[index + 1]?.bannerTop ?? height
    stage.theme = stage.stageIndex % 4
    stage.rangeTop = stage.bannerTop
    stage.rangeBottom = Math.max(stage.bannerTop + 80, nextTop)
  })

  const activeNode = nodes.find((n) => n.isActive) || null

  return {
    width: safeWidth,
    height,
    nodes,
    stages,
    pathSegments,
    // 兼容旧 QuestMap 字段名
    intraSegments: pathSegments.filter((s) => s.kind === 'intra'),
    interSegments: pathSegments.filter((s) => s.kind === 'inter'),
    centerX,
    contentLeft: SIDE_PAD,
    totalNodeCount: activeSections.reduce((n, s) => n + (s.items?.length || 0), 0),
    activeNode,
  }
}

export function buildSnakePathD(seg) {
  if (!seg) return ''
  const midY = (seg.y1 + seg.y2) / 2
  const ctrl = Math.min(56, Math.abs(seg.x2 - seg.x1) * 0.45)
  const dir = seg.x2 >= seg.x1 ? 1 : -1
  return `M ${seg.x1} ${seg.y1} C ${seg.x1} ${midY - 8}, ${seg.x2 - dir * ctrl} ${midY + 8}, ${seg.x2} ${seg.y2}`
}

/** @deprecated 保留给旧调用；蛇形布局统一用 buildSnakePathD */
export function buildIntraPathD(seg) {
  return buildSnakePathD(seg)
}

/** @deprecated 保留给旧调用；蛇形布局统一用 buildSnakePathD */
export function buildInterPathD(seg) {
  return buildSnakePathD(seg)
}

export function defaultCollapsedStageKeys(sections, threshold = 12) {
  const active = (sections || []).filter((s) => (s.items || []).length > 0)
  const total = active.reduce((n, s) => n + s.items.length, 0)
  const keys = new Set()
  active.forEach((section, stageIndex) => {
    const stageKey = `${section.no ?? 'x'}-${stageIndex}`
    // 未分组阶段无条件默认折叠，避免存量作业铺满地图
    if (Number(section.no) === 0 && section.items.length > 1) {
      keys.add(stageKey)
      return
    }
    if (total > threshold && stageIndex >= 2 && section.items.length > 1) {
      keys.add(stageKey)
    }
  })
  return keys
}

function round1(n) {
  return Math.round(n * 10) / 10
}

/**
 * 一条穿过全部关卡圆心的小路。
 * 每段先竖直离开关卡，再横摆，最后竖直进入下一关，避免拉成直线。
 * 控制点的 y 保持递增，方便按弧长二分。
 */
export function buildFullPathD(nodes) {
  const pts = (nodes || []).map((node) => ({ x: Number(node.x) || 0, y: Number(node.y) || 0 }))
  if (pts.length < 2) return ''
  const parts = [`M ${round1(pts[0].x)} ${round1(pts[0].y)}`]
  for (let i = 0; i < pts.length - 1; i += 1) {
    const p1 = pts[i]
    const p2 = pts[i + 1]
    const dy = p2.y - p1.y
    const dx = p2.x - p1.x
    const wide = Math.abs(dx) > Math.max(180, Math.abs(dy) * 1.35)
    const sway = (i % 2 === 0 ? 1 : -1) * Math.min(24, Math.abs(dx) * 0.08)
    const c1x = p1.x + sway
    const c1y = p1.y + dy * (wide ? 0.7 : 0.5)
    const c2x = p2.x + sway
    const c2y = p1.y + dy * (wide ? 0.86 : 0.5)
    parts.push(`C ${round1(c1x)} ${round1(c1y)}, ${round1(c2x)} ${round1(c2y)}, ${round1(p2.x)} ${round1(p2.y)}`)
  }
  return parts.join(' ')
}

/**
 * 用路径的 getPointAtLength 二分，求每个节点在曲线上的弧长。
 * 关卡纵向单调，按 y 搜索即可。
 */
export function measureNodeArcLengths(pathEl, nodes) {
  if (!pathEl || !nodes?.length) return []
  const total = pathEl.getTotalLength()
  if (!Number.isFinite(total) || total <= 0) return nodes.map(() => 0)
  let cursor = 0
  return nodes.map((node, index) => {
    if (index === 0) return 0
    let low = cursor
    let high = total
    const targetY = Number(node.y) || 0
    for (let step = 0; step < 22; step += 1) {
      const mid = (low + high) / 2
      const point = pathEl.getPointAtLength(mid)
      if (point.y < targetY) low = mid
      else high = mid
    }
    cursor = (low + high) / 2
    return cursor
  })
}

/** 根据滚动位置推断当前阶段（用于 Sticky 横幅） */
export function resolveStickyStage(stages, scrollTop, offset = 12) {
  if (!stages?.length) return null
  let current = stages[0]
  for (const stage of stages) {
    if ((stage.bannerTop || 0) - offset <= scrollTop) {
      current = stage
    } else {
      break
    }
  }
  return current
}
